import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableContainer,
  TableRow,
  Paper,
  Button,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  IconButton,
  FormGroup,
  FormControlLabel,
  Checkbox,
  LinearProgress,
  Divider,
} from '@mui/material';
import { Receipt, Download, Business, Visibility, Edit, ChevronLeft, ChevronRight, PictureAsPdf, CalendarViewMonth, List as ListIcon, Settings } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { payslipsAPI, employeesAPI, salaryDetailsAPI, companiesAPI, attendanceAPI } from '../services/api';

const Payslip = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [payslip, setPayslip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  // Admin/HR specific state
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showEmployeeList, setShowEmployeeList] = useState(true);
  const [showPayslipForm, setShowPayslipForm] = useState(false);
  const [showPayslipList, setShowPayslipList] = useState(false);
  const [payslipList, setPayslipList] = useState([]);
  const [sortBy, setSortBy] = useState('employee_id');
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [viewMode, setViewMode] = useState('list');
  const [selectedMonthFilter, setSelectedMonthFilter] = useState('all');
  const [isEditing, setIsEditing] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [payslipFormData, setPayslipFormData] = useState({
      basic: '',
      daily_allowance: '',
      house_rent_allowance: '',
      telephone_allowance: '',
      medical_allowance: '',
      food_allowance: '',
      others_allowance: '',
      overtime: '',
      loyalty_bonus: '',
      advance_payment: '',
      other_deduction: '',
      professional_tax: '',
      tds: '',
      epf: '',
      esi: '',
      work_days: '',
      present_days: '',
      absent_days: '',
      leave_days: '',
      unpaid_leave_days: '',
      sick_leave_taken: '',
      paid_leave_taken: '',
      unpaid_leave_taken: '',
      leave_earned: '',
      balance_last: '',
      balance_current: '',
      ctc_month: '',
      ctc_year: '',
      remarks: '',
    });

  // Current Month Overview states
  const [currentMonthData, setCurrentMonthData] = useState(null);
  const [showShiftConfig, setShowShiftConfig] = useState(false);
  const [shiftData, setShiftData] = useState(null);
  const [shiftFormData, setShiftFormData] = useState({
    weekend_off: true,
    first_saturday_work: false,
    working_days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday']
  });

  const isAdminOrHR = user?.role === 'admin' || user?.role === 'hr';

  // Calculate working days based on shift configuration
  const calculateWorkingDaysInMonth = useCallback((month, year, shift) => {
    const startDate = new Date(year, month - 1, 1);
    const endDate = new Date(year, month, 0);
    
    let workingDays = 0;
    let weekendCount = 0;
    let weekendDays = [];
    
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const workingDaysArray = shift?.working_days || ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
    const weekendOff = shift?.weekend_off !== false;
    const firstSaturdayWork = shift?.first_saturday_work || false;
    
    let firstSaturdayDate = null;
    
    for (let d = 1; d <= endDate.getDate(); d++) {
      const date = new Date(year, month - 1, d);
      const dayOfWeek = date.getDay();
      const dayName = dayNames[dayOfWeek];
      
      let isWorkingDay = workingDaysArray.includes(dayName);
      
      if (dayOfWeek === 6 && !firstSaturdayDate) {
        firstSaturdayDate = d;
      }
      
      if (weekendOff) {
        if (dayOfWeek === 0) {
          isWorkingDay = false;
          weekendDays.push({ date: d, day: 'Sunday' });
          weekendCount++;
        } else if (dayOfWeek === 6) {
          if (firstSaturdayWork && d === firstSaturdayDate) {
            isWorkingDay = true;
          } else {
            isWorkingDay = false;
            weekendDays.push({ date: d, day: 'Saturday' });
            weekendCount++;
          }
        }
      }
      
      if (isWorkingDay) {
        workingDays++;
      }
    }
    
    return { workingDays, weekendCount, weekendDays };
  }, []);

  const getPayslipPrintStyles = () => `
    * { margin: 0; padding: 0; box-sizing: border-box; }
    @page { size: A4; margin: 10mm; }
    body {
      font-family: Inter, "Segoe UI", Arial, sans-serif;
      background: #eef2f6;
      color: #172033;
      font-size: 11px;
      line-height: 1.45;
      padding: 12px;
    }
    .payslip-container {
      max-width: 210mm;
      min-height: 270mm;
      margin: 0 auto;
      background: #ffffff;
      border: 1px solid #5dad08;
      border-radius: 8px;
      overflow: hidden;
      box-shadow: 0 18px 45px rgba(23, 32, 51, 0.14);
    }
    .header {
      background: #172033;
      color: #5dad08;
      padding: 18px 22px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      border-bottom: 4px solid #5dad08;
    }
    .header-left { display: flex; align-items: center; gap: 13px; min-width: 0; }
    .logo {
      width: 100px;
      height: 100px;
      border-radius: 8px;
      background: #ffffff00;
      padding: 6px;
      object-fit: contain;
      border: 1px solid #5dad08;
    }
    .company-info { text-align: left; min-width: 0; }
    .company-name {
      font-size: 18px;
      font-weight: 800;
      letter-spacing: 0.4px;
      text-transform: uppercase;
      line-height: 1.2;
    }
    .company-address {
      font-size: 10px;
      color: #d9e0ea;
      margin-top: 4px;
      max-width: 420px;
    }
    .header-right { text-align: right; }
    .payslip-title {
      display: inline-block;
      font-size: 12px;
      font-weight: 800;
      letter-spacing: 1.5px;
      text-transform: uppercase;
      color: #5dad08;
      background: #172033;
      padding: 7px 13px;
      border-radius: 6px;
    }
    .title-bar {
      background: #f7f9fc;
      padding: 12px 22px;
      border-bottom: 1px solid #d9e0ea;
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 14px;
    }
    .title-bar h2 {
      color: #172033;
      font-size: 15px;
      font-weight: 800;
      margin: 0;
    }
    .title-bar span {
      color: #627087;
      font-size: 10px;
      white-space: nowrap;
    }
    .content { padding: 18px 22px; }
    .employee-info {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 12px;
      margin-bottom: 16px;
    }
    .info-card {
      background: #fbfcfe;
      border: 1px solid #d9e0ea;
      border-radius: 8px;
      padding: 11px 13px;
    }
    .info-card h4 {
      color: #172033;
      font-size: 10px;
      text-transform: uppercase;
      letter-spacing: 0.8px;
      margin-bottom: 8px;
      padding-bottom: 6px;
      border-bottom: 1px solid #e7ecf3;
    }
    .info-card p {
      font-size: 10px;
      color: #4c5a70;
      margin-bottom: 5px;
    }
    .info-card strong { color: #172033; font-weight: 700; }
    .salary-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 14px;
      margin-bottom: 16px;
    }
    .salary-table {
      width: 100%;
      border-collapse: separate;
      border-spacing: 0;
      font-size: 10px;
      border: 1px solid #d9e0ea;
      border-radius: 8px;
      overflow: hidden;
    }
    .salary-table th {
      background: #172033;
      color: #ffffff;
      padding: 9px 10px;
      text-align: left;
      font-size: 9px;
      letter-spacing: 0.8px;
      text-transform: uppercase;
    }
    .income-table th { border-bottom: 3px solid #13ff00; }
    .deduction-table th { border-bottom: 3px solid #e00f0f; }
    .salary-table td {
      padding: 7px 10px;
      border-bottom: 1px solid #e7ecf3;
      color: #344154;
    }
    .salary-table tr:nth-child(even) td { background: #fbfcfe; }
    .salary-table tr:last-child td { border-bottom: none; }
    .salary-table .amount {
      text-align: right;
      font-family: "Segoe UI", Arial, sans-serif;
      font-weight: 650;
      color: #172033;
      white-space: nowrap;
    }
    .salary-table .total-row td {
      font-weight: 800;
      background: #f1f5f9;
      border-top: 1px solid #cbd5e1;
    }
    .income-table .total-row td { color: #0cad00; }
    .deduction-table .total-row td { color: #e00f0f; }
    .summary-box {
      background: #172033;
      color: white;
      padding: 14px;
      border-radius: 8px;
      margin-bottom: 16px;
      border: 1px solid #263149;
    }
    .summary-box h3 {
      font-size: 10px;
      color: #d9e0ea;
      letter-spacing: 0.9px;
      text-transform: uppercase;
      margin-bottom: 10px;
    }
    .summary-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 10px;
      text-align: left;
    }
    .summary-item {
      background: rgba(255,255,255,0.08);
      padding: 10px;
      border-radius: 8px;
      border: 1px solid rgba(43, 179, 5, 0.6);
    }
    .summary-item .label {
      font-size: 8px;
      color: #cbd5e1;
      margin-bottom: 5px;
      text-transform: uppercase;
      letter-spacing: 0.7px;
    }
    .summary-item .value {
      font-size: 15px;
      font-weight: 800;
      color: #ffffff;
    }
    .summary-item.net-salary {
      background: #172033;
      color: #0cad00;
      border-color: #0cad00;
    }
    .summary-item.net-salary .label,
    .summary-item.net-salary .value { color: #0cad00; }
    .summary-item.net-salary .value { font-size: 18px; }
    .attendance-section {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 12px;
      margin-bottom: 12px;
    }
    .attendance-card {
      background: #fbfcfe;
      border: 1px solid #0cad00;
      border-radius: 8px;
      padding: 11px 13px;
    }
    .attendance-card h4 {
      color: #172033;
      font-size: 10px;
      margin-bottom: 8px;
      text-transform: uppercase;
      letter-spacing: 0.8px;
    }
    .attendance-row {
      display: flex;
      justify-content: space-between;
      gap: 12px;
      padding: 6px 0;
      border-bottom: 1px dashed #cbd5e1;
      font-size: 10px;
      color: #4c5a70;
    }
    .attendance-row:last-child { border-bottom: none; }
    .attendance-row strong { color: #172033; }
    .remarks {
      margin-top: 10px;
      padding: 10px 12px;
      background: #fff8dc;
      border: 1px solid #f1d98a;
      border-radius: 8px;
      font-size: 10px;
      color: #4c4220;
    }
    .footer {
      text-align: center;
      padding: 12px 8px 2px;
      border-top: 1px solid #e7ecf3;
      margin-top: 12px;
    }
    .footer p { font-size: 9px; color: #627087; margin: 2px 0; }
    @media print {
      body { padding: 0; background: white; }
      .payslip-container { box-shadow: none; border-radius: 0; border-color: #d9e0ea; }
    }
  `;

  const loadEmployees = useCallback(async () => {
    try {
      const response = await employeesAPI.getAll({ limit: 100 });
      if (response.data.success) {
        setEmployees(response.data.data.employees || []);
      }
    } catch (error) {
      console.error('Error loading employees:', error);
      setEmployees([]);
    }
  }, []);

  // Load employees on mount
  useEffect(() => {
    if (isAdminOrHR) {
      loadEmployees();
    }
  }, [isAdminOrHR, loadEmployees]);

  // Check for employee param in URL
  useEffect(() => {
    const employeeId = searchParams.get('employee');
    if (employeeId && isAdminOrHR && employees.length > 0) {
      const employee = employees.find(emp => emp.id === parseInt(employeeId));
      if (employee) {
        setSelectedEmployee(employee);
        setShowEmployeeList(false);
      }
    }
  }, [employees, isAdminOrHR, searchParams]);

  const loadPayslip = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const employeeId = isAdminOrHR && selectedEmployee ? selectedEmployee.id : user?.id;

      if (!employeeId) {
        setError('Employee ID not found');
        setLoading(false);
        return;
      }

      // Fetch payslip from backend
      let response;
      if (isAdminOrHR && selectedEmployee) {
        response = await payslipsAPI.getAll({
          employee_id: selectedEmployee.id,
          month: selectedMonth,
          year: selectedYear
        });
      } else {
        response = await payslipsAPI.getMyPayslip({
          month: selectedMonth,
          year: selectedYear
        });
      }

      if (response.data.success && response.data.data) {
        // getAll returns an array, getMyPayslip returns a single object
        const existingPayslip = Array.isArray(response.data.data)
          ? response.data.data[0]
          : response.data.data;

        // Check if payslip exists
        if (!existingPayslip) {
          setPayslip(null);
          setLoading(false);
          return;
        }

        const empId = isAdminOrHR && selectedEmployee ? selectedEmployee.id : user?.id;

        // Fetch employee details
        const employeeResponse = await employeesAPI.getById(empId);
        const employee = employeeResponse.data.data.employee;
        
        // Set shift configuration from employee's assigned shift
        if (employee?.shift) {
          setShiftFormData({
            weekend_off: employee.shift.weekend_off !== false,
            first_saturday_work: employee.shift.first_saturday_work === true,
            working_days: employee.shift.working_days || ['monday', 'tuesday', 'wednesday', 'thursday', 'friday']
          });
        }
        
        // Fetch company details
        let company = null;
        try {
          if (employee.company_id) {
            const companyResponse = await companiesAPI.getById(employee.company_id);
            if (companyResponse.data.success) {
              company = companyResponse.data.data;
            }
          } else {
            const companiesResponse = await companiesAPI.getActive();
            if (companiesResponse.data.success && companiesResponse.data.data.length > 0) {
              company = companiesResponse.data.data[0];
            }
          }
        } catch (companyError) {
          console.error('Error loading company:', companyError);
        }

        // Calculate totals
        const grossIncome = (parseFloat(existingPayslip.basic) || 0) +
          (parseFloat(existingPayslip.daily_allowance) || 0) +
          (parseFloat(existingPayslip.house_rent_allowance) || 0) +
          (parseFloat(existingPayslip.telephone_allowance) || 0) +
          (parseFloat(existingPayslip.medical_allowance) || 0) +
          (parseFloat(existingPayslip.food_allowance) || 0) +
          (parseFloat(existingPayslip.others_allowance) || 0) +
          (parseFloat(existingPayslip.overtime) || 0) +
          (parseFloat(existingPayslip.loyalty_bonus) || 0);

        const grossDeduction = (parseFloat(existingPayslip.advance_payment) || 0) +
          (parseFloat(existingPayslip.other_deduction) || 0) +
          (parseFloat(existingPayslip.professional_tax) || 0) +
          (parseFloat(existingPayslip.tds) || 0) +
          (parseFloat(existingPayslip.epf) || 0) +
          (parseFloat(existingPayslip.esi) || 0) +
          (parseFloat(existingPayslip.unpaid_leave_deduction) || 0) +
          (parseFloat(existingPayslip.absence_deduction) || 0);

        setPayslip({
          id: existingPayslip.id,
          companyName: company?.name || 'Company Name',
          companyAddress: company?.address || 'Company Address',
          companyLogo: company?.logo,
          salaryMonth: new Date(selectedYear, selectedMonth - 1).toLocaleString('default', { month: 'long' }),
          salaryYear: selectedYear,
          salaryDate: new Date().toLocaleDateString('en-IN'),
          status: existingPayslip.status,
          employee: {
            id: employee.employee_id,
            name: `${employee.first_name} ${employee.last_name}`,
            pan: employee.pan || 'N/A',
            designation: employee.designation || 'N/A',
            bankName: employee.bank_name || 'N/A',
            bankAccount: employee.bank_account || 'N/A',
            joiningDate: employee.join_date ? new Date(employee.join_date).toLocaleDateString('en-IN') : 'N/A',
            paymentMode: employee.payment_mode || 'Bank',
          },
          income: {
            basic: parseFloat(existingPayslip.basic) || 0,
            dailyAllowance: parseFloat(existingPayslip.daily_allowance) || 0,
            houseRentAllowance: parseFloat(existingPayslip.house_rent_allowance) || 0,
            telephoneAllowance: parseFloat(existingPayslip.telephone_allowance) || 0,
            medicalAllowance: parseFloat(existingPayslip.medical_allowance) || 0,
            foodAllowance: parseFloat(existingPayslip.food_allowance) || 0,
            othersAllowance: parseFloat(existingPayslip.others_allowance) || 0,
            overtime: parseFloat(existingPayslip.overtime) || 0,
            loyaltyBonus: parseFloat(existingPayslip.loyalty_bonus) || 0,
          },
          deductions: {
            advancePayment: parseFloat(existingPayslip.advance_payment) || 0,
            otherDeduction: parseFloat(existingPayslip.other_deduction) || 0,
            professionalTax: parseFloat(existingPayslip.professional_tax) || 0,
            tds: parseFloat(existingPayslip.tds) || 0,
            epf: parseFloat(existingPayslip.epf) || 0,
            esi: parseFloat(existingPayslip.esi) || 0,
            unpaidLeaveDeduction: parseFloat(existingPayslip.unpaid_leave_deduction) || 0,
            absenceDeduction: parseFloat(existingPayslip.absence_deduction) || 0,
          },
          attendance: {
            workDays: parseInt(existingPayslip.work_days) || 0,
            present: parseInt(existingPayslip.present_days) || 0,
            absent: parseInt(existingPayslip.absent_days) || 0,
          },
          leave: {
            leaveEarned: parseFloat(existingPayslip.leave_earned) || 0,
            balanceLast: parseFloat(existingPayslip.balance_last) || 0,
            balanceCurrent: parseFloat(existingPayslip.balance_current) || 0,
            sickLeaveTaken: parseFloat(existingPayslip.sick_leave_taken) || 0,
            paidLeaveTaken: parseFloat(existingPayslip.paid_leave_taken) || 0,
            unpaidLeaveTaken: parseFloat(existingPayslip.unpaid_leave_taken) || 0,
            totalLeaveDays: parseFloat(existingPayslip.sick_leave_taken || 0) + parseFloat(existingPayslip.paid_leave_taken || 0) + parseFloat(existingPayslip.unpaid_leave_taken || 0),
          },
          leaveBalances: { sick: 0, paid: 0, unpaid: 0 },
          grossIncome: grossIncome,
          grossDeduction: grossDeduction,
          netSalary: Math.round(grossIncome - grossDeduction),
          advance_payment: existingPayslip.advance_payment || 0,
          other_deduction: existingPayslip.other_deduction || 0,
          professional_tax: existingPayslip.professional_tax || 0,
          tds: existingPayslip.tds || 0,
          epf: existingPayslip.epf || 0,
          esi: existingPayslip.esi || 0,
          unpaid_leave_deduction: existingPayslip.unpaid_leave_deduction || 0,
          absence_deduction: existingPayslip.absence_deduction || 0,
          unpaid_leave_days: existingPayslip.unpaid_leave_days || 0,
          leave_days: existingPayslip.leave_days || 0,
          ctcMonth: existingPayslip.ctc_month || 0,
          ctcYear: existingPayslip.ctc_year || 0,
          remarks: existingPayslip.remarks || '',
        });
      } else {
        setPayslip(null);
      }
    } catch (error) {
      console.error('Error loading payslip:', error);
      setError('Failed to load payslip');
      setPayslip(null);
    } finally {
      setLoading(false);
    }
  }, [isAdminOrHR, selectedEmployee, selectedMonth, selectedYear, user?.id]);

  // Load payslip when month/year/employee changes
  useEffect(() => {
    if (!isAdminOrHR || selectedEmployee) {
      loadPayslip();
    }
  }, [selectedMonth, selectedYear, selectedEmployee, isAdminOrHR, loadPayslip]);

  // Calculate current month overview data
  useEffect(() => {
    const loadCurrentMonthData = async () => {
      try {
        const employeeId = isAdminOrHR && selectedEmployee ? selectedEmployee.id : user?.id;
        if (!employeeId) return;

        // Get working days from payslip or calculate from shift config
        const targetWorkingDays = payslip?.attendance?.workDays || payslip?.work_days || calculateWorkingDaysInMonth(selectedMonth, selectedYear, shiftFormData).workingDays;

        // Fetch attendance summary for additional metrics - use employee-specific endpoint for admin/HR
        let attendanceResponse;
        if (isAdminOrHR && selectedEmployee) {
          attendanceResponse = await attendanceAPI.getEmployeeMonthlyStats(selectedEmployee.id, selectedMonth, selectedYear).catch(() => ({ data: { data: null } }));
        } else {
          attendanceResponse = await attendanceAPI.getStats(employeeId, selectedMonth, selectedYear).catch(() => ({ data: { data: null } }));
        }

        const responseData = attendanceResponse.data?.data || {};
        // Dashboard API returns attendance at responseData.attendance for employee summary
        const attendance = responseData.attendance || responseData;
        const presentDays = attendance?.present || 0;

        // Calculate attendance rate based on payslip data or attendance API data
        const workingDaysForRate = payslip?.attendance?.workDays || payslip?.work_days || targetWorkingDays;
        const presentDaysForRate = payslip?.attendance?.present || payslip?.present_days || presentDays;
        const attendanceRate = workingDaysForRate > 0 ? Math.round((presentDaysForRate / workingDaysForRate) * 100) : 0;

        setCurrentMonthData({
          workingDays: targetWorkingDays,
          weekendCount: calculateWorkingDaysInMonth(selectedMonth, selectedYear, shiftFormData).weekendCount,
          attendanceRate,
          presentDays: payslip?.attendance?.present || payslip?.present_days || presentDays,
          absentDays: payslip?.attendance?.absent || payslip?.absent_days || 0,
          lateDays: attendance?.late || 0,
          activeDays: presentDays,
          workHours: attendance?.total_working_hours || 0,
          breakTime: attendance?.total_break_hours || 0
        });
      } catch (error) {
        console.error('Error loading current month data:', error);
      }
    };

    loadCurrentMonthData();
  }, [selectedMonth, selectedYear, shiftFormData, payslip, isAdminOrHR, selectedEmployee, user?.id]);

  // Calculate total income
  const totalIncome = useMemo(() => {
    const incomeFields = ['basic', 'daily_allowance', 'house_rent_allowance', 'telephone_allowance', 'medical_allowance', 'food_allowance', 'others_allowance', 'overtime', 'loyalty_bonus'];
    let total = 0;
    incomeFields.forEach(field => {
      const value = parseFloat(payslipFormData[field]) || 0;
      total += value;
    });
    return total;
  }, [payslipFormData]);

  // Calculate per day income and deductions
  const { perDayIncome, unpaidLeaveDeduction, absenceDeductionCalc, ctcMonth } = useMemo(() => {
    const workDays = parseFloat(payslipFormData.work_days) || 1;
    const perDay = workDays > 0 ? totalIncome / workDays : 0;
    const unpaidLeaveDays = parseFloat(payslipFormData.unpaid_leave_days) || 0;
    const absentDays = parseFloat(payslipFormData.absent_days) || 0;
    
    return {
      perDayIncome: perDay,
      unpaidLeaveDeduction: unpaidLeaveDays * perDay,
      absenceDeductionCalc: absentDays * perDay,
      ctcMonth: totalIncome.toFixed(2),
    };
  }, [totalIncome, payslipFormData.work_days, payslipFormData.unpaid_leave_days, payslipFormData.absent_days]);

  const loadPayslipList = useCallback(async () => {
    try {
      setLoading(true);
      const response = await payslipsAPI.getAll({ limit: 500 });
      if (response.data.success) {
        setPayslipList(response.data.data || []);
      }
    } catch (error) {
      console.error('Error loading payslip list:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const payslipsByMonth = useMemo(() => {
    const grouped = {};
    payslipList.forEach(p => {
      if (p && p.month && p.year) {
        const key = `${p.year}-${String(p.month).padStart(2, '0')}`;
        if (!grouped[key]) {
          grouped[key] = {
            month: p.month,
            year: p.year,
            monthName: new Date(0, p.month - 1).toLocaleString('default', { month: 'long' }),
            payslips: []
          };
        }
        grouped[key].payslips.push(p);
      }
    });
    return Object.values(grouped).sort((a, b) => new Date(b.year, b.month - 1) - new Date(a.year, a.month - 1));
  }, [payslipList]);

  const availableMonths = useMemo(() => {
    return payslipsByMonth.map(m => `${m.year}-${String(m.month).padStart(2, '0')}`);
  }, [payslipsByMonth]);

  const handleDownloadPayslip = async (payslipItem) => {
    try {
      const employee = payslipItem.employee;
      const employeeDbId = payslipItem.employee_id;
      if (!employeeDbId) return;

      const response = await payslipsAPI.getAll({
        employee_id: employeeDbId,
        month: payslipItem.month,
        year: payslipItem.year
      });

      if (response.data.success && response.data.data.length > 0) {
        const payslipData = response.data.data[0];
        
        const employeeResponse = await employeesAPI.getById(employeeDbId);
        const emp = employeeResponse.data.data.employee;
        
        let company = null;
        try {
          if (emp.company_id) {
            const companyResponse = await companiesAPI.getById(emp.company_id);
            if (companyResponse.data.success) {
              company = companyResponse.data.data;
            }
          } else {
            const companiesResponse = await companiesAPI.getActive();
            if (companiesResponse.data.success && companiesResponse.data.data.length > 0) {
              company = companiesResponse.data.data[0];
            }
          }
        } catch (companyError) {
          console.error('Error loading company:', companyError);
        }

        const grossIncome = (parseFloat(payslipData.basic) || 0) +
          (parseFloat(payslipData.daily_allowance) || 0) +
          (parseFloat(payslipData.house_rent_allowance) || 0) +
          (parseFloat(payslipData.telephone_allowance) || 0) +
          (parseFloat(payslipData.medical_allowance) || 0) +
          (parseFloat(payslipData.food_allowance) || 0) +
          (parseFloat(payslipData.others_allowance) || 0) +
          (parseFloat(payslipData.overtime) || 0) +
(parseFloat(payslipData.loyalty_bonus) || 0);

        const grossDeduction = (parseFloat(payslipData.advance_payment) || 0) +
          (parseFloat(payslipData.other_deduction) || 0) +
          (parseFloat(payslipData.professional_tax) || 0) +
          (parseFloat(payslipData.tds) || 0) +
          (parseFloat(payslipData.epf) || 0) +
          (parseFloat(payslipData.esi) || 0) +
          (parseFloat(payslipData.unpaid_leave_deduction) || 0) +
          (parseFloat(payslipData.absence_deduction) || 0);

        const formatAmount = (amount) => {
          const num = parseFloat(amount) || 0;
          return num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        };

        const printWindow = window.open('', '_blank');
        if (printWindow) {
          printWindow.document.write(`
            <!DOCTYPE html>
            <html>
              <head>
                <title>Payslip - ${emp.first_name} ${emp.last_name}</title>
                <style>
                  * { margin: 0; padding: 0; box-sizing: border-box; }
                  @page { size: A4; margin: 10mm; }
                  body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; padding: 10px; background: #fff; font-size: 11px; line-height: 1.3; }
                  .payslip-container { max-width: 210mm; margin: 0 auto; background: #fff; }
                  .header { background: linear-gradient(135deg, #6e718d 0%, #303f9f 100%); color: white; padding: 12px 15px; text-align: center; display: flex; align-items: center; justify-content: space-between; }
                  .header-left { display: flex; align-items: center; gap: 10px; }
                  .logo { width: 40px; height: 40px; border-radius: 50%; background: white; padding: 3px; object-fit: contain; }
                  .company-info { text-align: left; flex: 1; }
                  .company-name { font-size: 16px; font-weight: 700; letter-spacing: 0.5px; text-transform: uppercase; }
                  .company-address { font-size: 10px; opacity: 0.9; }
                  .header-right { text-align: right; }
                  .payslip-title { font-size: 13px; font-weight: 600; background: rgba(255,255,255,0.15); padding: 4px 12px; border-radius: 3px; }
                  .title-bar { background: #fce4ec; padding: 8px 15px; border-bottom: 2px solid #e91e63; display: flex; justify-content: space-between; align-items: center; }
                  .title-bar h2 { color: #c2185b; font-size: 14px; font-weight: 600; margin: 0; }
                  .title-bar span { color: #666; font-size: 10px; }
                  .content { padding: 12px 15px; }
                  .employee-info { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 12px; }
                  .info-card { background: #f8f9fa; border-left: 3px solid #1976d2; padding: 8px 10px; border-radius: 3px; }
                  .info-card h4 { color: #1565c0; font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 6px; }
                  .info-card p { font-size: 10px; color: #424242; margin-bottom: 3px; }
                  .info-card strong { color: #212121; }
                  .salary-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; margin-bottom: 12px; }
                  .salary-table { width: 100%; border-collapse: collapse; font-size: 10px; }
                  .salary-table th { color: white; padding: 6px 8px; text-align: left; font-size: 9px; text-transform: uppercase; }
                  .income-table th { background: #2e7d32; }
                  .deduction-table th { background: #c62828; }
                  .salary-table td { padding: 4px 8px; border-bottom: 1px solid #e0e0e0; }
                  .salary-table tr:nth-child(even) { background: #f8f9fa; }
                  .salary-table .amount { text-align: right; font-family: 'Courier New', monospace; }
                  .salary-table .total-row { font-weight: 700; }
                  .income-table .total-row { background: #e8f5e9; }
                  .income-table .total-row td { border-top: 1px solid #2e7d32; }
                  .deduction-table .total-row { background: #ffcdd2; }
                  .deduction-table .total-row td { border-top: 1px solid #c62828; }
                  .summary-box { background: linear-gradient(135deg, #1a237e 0%, #303f9f 100%); color: white; padding: 10px 15px; border-radius: 4px; margin-bottom: 12px; }
                  .summary-box h3 { font-size: 11px; opacity: 0.9; margin-bottom: 8px; }
                  .summary-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; text-align: center; }
                  .summary-item { background: rgba(255,255,255,0.1); padding: 8px; border-radius: 3px; }
                  .summary-item .label { font-size: 9px; opacity: 0.8; margin-bottom: 3px; text-transform: uppercase; }
                  .summary-item .value { font-size: 14px; font-weight: 700; }
                  .summary-item.net-salary { background: #ffc107; color: #212121; }
                  .summary-item.net-salary .value { font-size: 16px; }
                  .attendance-section { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 10px; }
                  .attendance-card { background: #f8f9fa; border-radius: 3px; padding: 8px; }
                  .attendance-card h4 { color: #1565c0; font-size: 10px; margin-bottom: 6px; text-transform: uppercase; }
                  .attendance-row { display: flex; justify-content: space-between; padding: 3px 0; border-bottom: 1px dashed #e0e0e0; font-size: 10px; }
                  .attendance-row:last-child { border-bottom: none; }
                  .footer { text-align: center; padding: 8px; border-top: 1px solid #e0e0e0; margin-top: 8px; }
                  .footer p { font-size: 9px; color: #757575; margin: 2px 0; }
                  .remarks { margin-top: 8px; padding: 6px; background: #fff3e0; border-radius: 3px; font-size: 10px; }
                  ${getPayslipPrintStyles()}
                </style>
              </head>
              <body>
                <div class="payslip-container">
                  <div class="header">
                    <div class="header-left">
                      ${company?.logo ? `<img src="${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5076'}/uploads/companies/${company.logo}" alt="Company Logo" class="logo" />` : ''}
                      <div class="company-info">
                        <div class="company-name">${company?.name || 'Company Name'}</div>
                        <div class="company-address">${company?.address || 'Company Address'}</div>
                      </div>
                    </div>
                    <div class="header-right">
                      <div class="payslip-title">Salary Slip</div>
                    </div>
                  </div>

                  <div class="title-bar">
                    <h2>Salary for ${new Date(0, payslipData.month - 1).toLocaleString('default', { month: 'long' })} ${payslipData.year}</h2>
                    <span>ID: ${payslipData.id} | ${new Date().toLocaleDateString('en-IN')}</span>
                  </div>

                  <div class="content">
                    <div class="employee-info">
                      <div class="info-card">
                        <h4>Employee Details</h4>
                        <p><strong>ID:</strong> ${emp.employee_id} | <strong>Name:</strong> ${emp.first_name} ${emp.last_name}</p>
                        <p><strong>Desig:</strong> ${emp.designation || 'N/A'} | <strong>Join:</strong> ${emp.join_date ? new Date(emp.join_date).toLocaleDateString('en-IN') : 'N/A'}</p>
                      </div>
                      <div class="info-card">
                        <h4>Payment Details</h4>
                        <p><strong>PAN:</strong> ${emp.pan || 'N/A'} | <strong>Mode:</strong> ${emp.payment_mode || 'Bank'}</p>
                        <p><strong>Bank:</strong> ${emp.bank_name || 'N/A'} | <strong>A/C:</strong> ${emp.bank_account || 'N/A'}</p>
                      </div>
                    </div>

                    <div class="salary-grid">
                      <table class="salary-table income-table">
                        <thead>
                          <tr><th>Earnings</th><th style="text-align:right">Amount (₹)</th></tr>
                        </thead>
                        <tbody>
                          <tr><td>Basic Salary</td><td class="amount">${formatAmount(payslipData.basic)}</td></tr>
                          ${parseFloat(payslipData.daily_allowance) > 0 ? `<tr><td>Daily Allowance</td><td class="amount">${formatAmount(payslipData.daily_allowance)}</td></tr>` : ''}
                          ${parseFloat(payslipData.house_rent_allowance) > 0 ? `<tr><td>HRA</td><td class="amount">${formatAmount(payslipData.house_rent_allowance)}</td></tr>` : ''}
                          ${parseFloat(payslipData.medical_allowance) > 0 ? `<tr><td>Medical</td><td class="amount">${formatAmount(payslipData.medical_allowance)}</td></tr>` : ''}
                          ${parseFloat(payslipData.telephone_allowance) > 0 ? `<tr><td>Telephone</td><td class="amount">${formatAmount(payslipData.telephone_allowance)}</td></tr>` : ''}
                          ${parseFloat(payslipData.food_allowance) > 0 ? `<tr><td>Food Allowance</td><td class="amount">${formatAmount(payslipData.food_allowance)}</td></tr>` : ''}
                          ${parseFloat(payslipData.others_allowance) > 0 ? `<tr><td>Others</td><td class="amount">${formatAmount(payslipData.others_allowance)}</td></tr>` : ''}
                          ${parseFloat(payslipData.overtime) > 0 ? `<tr><td>Overtime</td><td class="amount">${formatAmount(payslipData.overtime)}</td></tr>` : ''}
                          ${parseFloat(payslipData.loyalty_bonus) > 0 ? `<tr><td>Loyalty Bonus</td><td class="amount">${formatAmount(payslipData.loyalty_bonus)}</td></tr>` : ''}
                          <tr class="total-row"><td><strong>Gross Earnings</strong></td><td class="amount"><strong>₹${grossIncome.toLocaleString('en-IN')}</strong></td></tr>
                        </tbody>
                      </table>

                      <table class="salary-table deduction-table">
                        <thead>
                          <tr><th>Deductions</th><th style="text-align:right">Amount (₹)</th></tr>
                        </thead>
                        <tbody>
                          ${parseFloat(payslipData.advance_payment) > 0 ? `<tr><td>Advance Payment</td><td class="amount">${formatAmount(payslipData.advance_payment)}</td></tr>` : ''}
                          ${parseFloat(payslipData.other_deduction) > 0 ? `<tr><td>Other Deduction</td><td class="amount">${formatAmount(payslipData.other_deduction)}</td></tr>` : ''}
                          ${parseFloat(payslipData.professional_tax) > 0 ? `<tr><td>Professional Tax</td><td class="amount">${formatAmount(payslipData.professional_tax)}</td></tr>` : ''}
                          ${parseFloat(payslipData.tds) > 0 ? `<tr><td>TDS</td><td class="amount">${formatAmount(payslipData.tds)}</td></tr>` : ''}
                          ${parseFloat(payslipData.epf) > 0 ? `<tr><td>EPF</td><td class="amount">${formatAmount(payslipData.epf)}</td></tr>` : ''}
                          ${parseFloat(payslipData.esi) > 0 ? `<tr><td>ESI</td><td class="amount">${formatAmount(payslipData.esi)}</td></tr>` : ''}
                          ${parseFloat(payslipData.unpaid_leave_deduction) > 0 ? `<tr><td>Unpaid Leave Deduction</td><td class="amount">${formatAmount(payslipData.unpaid_leave_deduction)}</td></tr>` : ''}
                          ${parseFloat(payslipData.absence_deduction) > 0 ? `<tr><td>Absence Deduction</td><td class="amount">${formatAmount(payslipData.absence_deduction)}</td></tr>` : ''}
                          <tr class="total-row"><td><strong>Total Deductions</strong></td><td class="amount"><strong>₹${grossDeduction.toLocaleString('en-IN')}</strong></td></tr>
                        </tbody>
                      </table>
                    </div>

                    <div class="summary-box">
                      <h3>Salary Summary</h3>
                      <div class="summary-grid">
                        <div class="summary-item">
                          <div class="label">Gross Salary</div>
                          <div class="value">₹${grossIncome.toLocaleString('en-IN')}</div>
                        </div>
                        <div class="summary-item">
                          <div class="label">Total Deductions</div>
                          <div class="value">₹${grossDeduction.toLocaleString('en-IN')}</div>
                        </div>
                        <div class="summary-item net-salary">
                          <div class="label">Net Salary</div>
                          <div class="value">₹${(grossIncome - grossDeduction).toLocaleString('en-IN')}</div>
                        </div>
                      </div>
                    </div>

                    <div class="attendance-section">
                      <div class="attendance-card">
                        <h4>Attendance</h4>
                        <div class="attendance-row"><span>Work Days</span><strong>${payslipData.work_days || 0}</strong></div>
                        <div class="attendance-row"><span>Present</span><strong>${payslipData.present_days || 0}</strong></div>
                        <div class="attendance-row"><span>Absent</span><strong>${payslipData.absent_days || 0}</strong></div>
                      </div>
                      <div class="attendance-card">
                        <h4>Leave / CTC</h4>
                        <div class="attendance-row"><span>Unpaid Leave</span><strong>${payslipData.unpaid_leave_days || 0}</strong></div>
                        <div class="attendance-row"><span>Total Leave</span><strong>${payslipData.leave_days || 0}</strong></div>
                        <div class="attendance-row"><span>CTC/Month</span><strong>₹${parseFloat(payslipData.ctc_month || 0).toLocaleString('en-IN')}</strong></div>
                      </div>
                    </div>

                    ${payslipData.remarks ? `<div class="remarks"><strong>Remarks:</strong> ${payslipData.remarks}</div>` : ''}

                    <div class="footer">
                      <p>This is a computer-generated document and does not require any signature.</p>
                    </div>
                  </div>
                </div>
              </body>
            </html>
          `);
          printWindow.document.close();
          printWindow.print();
        }
      }
    } catch (error) {
      console.error('Error downloading payslip:', error);
    }
  };

  const loadAttendanceSummary = useCallback(async (employeeId) => {
    try {
      // First try the detailed attendance-leave-details endpoint (provides full data including sick, paid, unpaid leave breakdown)
      const response = await payslipsAPI.getAttendanceLeaveDetails(employeeId, selectedMonth, selectedYear);
      if (response.data.success) {
        return response.data.data.current_values;
      }
    } catch (error) {
      console.error('Error loading attendance leave details:', error);
    }

    // Fallback to basic attendance summary
    try {
      const response = await payslipsAPI.getAttendanceSummary(employeeId, selectedMonth, selectedYear);
      if (response.data.success) {
        return response.data.data;
      }
    } catch (error) {
      console.error('Error loading attendance summary:', error);
    }
    return null;
  }, [selectedMonth, selectedYear]);

  const handleEmployeeSelect = (employee) => {
    setSelectedEmployee(employee);
    setShowEmployeeList(false);
  };

  const handleEditPayslip = useCallback(async (employee) => {
    setSelectedEmployee(employee);
    setIsEditing(true);
    setError('');

    const currentMonth = new Date().getMonth() + 1;
    const currentYear = new Date().getFullYear();

    try {
      // Load attendance summary including leave data from database using the improved endpoint
      let attendance = await loadAttendanceSummary(employee.id);
      
      // Try to load existing payslip for current month
      const response = await payslipsAPI.getAll({
        employee_id: employee.id,
        month: currentMonth,
        year: currentYear
      });

      if (response.data.success && response.data.data.length > 0) {
        const existingPayslip = response.data.data[0];
         setPayslipFormData({
            basic: existingPayslip.basic || '',
            daily_allowance: existingPayslip.daily_allowance || '',
            house_rent_allowance: existingPayslip.house_rent_allowance || '',
            telephone_allowance: existingPayslip.telephone_allowance || '',
            medical_allowance: existingPayslip.medical_allowance || '',
            food_allowance: existingPayslip.food_allowance || '',
            others_allowance: existingPayslip.others_allowance || '',
            overtime: existingPayslip.overtime || '',
            loyalty_bonus: existingPayslip.loyalty_bonus || '',
            advance_payment: existingPayslip.advance_payment || '',
            other_deduction: existingPayslip.other_deduction || '',
            professional_tax: existingPayslip.professional_tax || '',
            tds: existingPayslip.tds || '',
            epf: existingPayslip.epf || '',
            esi: existingPayslip.esi || '',
            work_days: existingPayslip.work_days || (attendance?.work_days || ''),
            present_days: existingPayslip.present_days || (attendance?.present_days || ''),
            absent_days: existingPayslip.absent_days || (attendance?.absent_days || ''),
            sick_leave_taken: existingPayslip.sick_leave_taken || (attendance?.sick_leave_taken || ''),
            paid_leave_taken: existingPayslip.paid_leave_taken || (attendance?.paid_leave_taken || ''),
            unpaid_leave_taken: existingPayslip.unpaid_leave_taken || (attendance?.unpaid_leave_taken || ''),
            leave_days: existingPayslip.leave_days || (attendance?.leave_days || ''),
            unpaid_leave_days: existingPayslip.unpaid_leave_days || (attendance?.unpaid_leave_days || ''),
            leave_earned: existingPayslip.leave_earned || (attendance?.leave_earned || '1'),
            balance_last: existingPayslip.balance_last || (attendance?.balance_last || '0'),
            balance_current: existingPayslip.balance_current || (attendance?.balance_current || '0'),
            ctc_month: existingPayslip.ctc_month || '',
            ctc_year: existingPayslip.ctc_year || '',
            remarks: existingPayslip.remarks || '',
          });
} else if (attendance) {
        // Load salary details to prefill income fields
        try {
          const salaryResponse = await salaryDetailsAPI.getByEmployee(employee.id);
          if (salaryResponse.data.success) {
            const salary = salaryResponse.data.data;
            setPayslipFormData({
              basic: salary.basic || '',
              daily_allowance: salary.daily_allowance || '',
              house_rent_allowance: salary.house_rent_allowance || '',
              telephone_allowance: salary.telephone_allowance || '',
              medical_allowance: salary.medical_allowance || '',
              food_allowance: salary.food_allowance || '',
              others_allowance: salary.others_allowance || '',
              overtime: '',
              loyalty_bonus: salary.loyalty_bonus || '',
              advance_payment: '',
              other_deduction: '',
              professional_tax: salary.professional_tax || '',
              tds: salary.tds || '',
              epf: salary.epf || '',
              esi: salary.esi || '',
              work_days: attendance.work_days || '',
              present_days: attendance.present_days || '',
              absent_days: attendance.absent_days || '',
              sick_leave_taken: attendance.sick_leave_taken || '',
              paid_leave_taken: attendance.paid_leave_taken || '',
              unpaid_leave_taken: attendance.unpaid_leave_taken || '',
              leave_days: attendance.leave_days || '',
              unpaid_leave_days: attendance.unpaid_leave_days || '',
              leave_earned: attendance.leave_earned || '1',
              balance_last: attendance.balance_last || '0',
              balance_current: attendance.balance_current || '0',
              ctc_month: '',
              ctc_year: '',
              remarks: '',
            });
          } else {
              setPayslipFormData({
                basic: '',
                daily_allowance: '',
                house_rent_allowance: '',
                telephone_allowance: '',
                medical_allowance: '',
                food_allowance: '',
                others_allowance: '',
                overtime: '',
                loyalty_bonus: '',
                advance_payment: '',
                other_deduction: '',
                professional_tax: '',
                tds: '',
                epf: '',
                esi: '',
                work_days: attendance.work_days || '',
                present_days: attendance.present_days || '',
                absent_days: attendance.absent_days || '',
                sick_leave_taken: attendance.sick_leave_taken || '',
                paid_leave_taken: attendance.paid_leave_taken || '',
                unpaid_leave_taken: attendance.unpaid_leave_taken || '',
                leave_days: attendance.leave_days || '',
                unpaid_leave_days: attendance.unpaid_leave_days || '',
                leave_earned: attendance.leave_earned || '1',
                balance_last: attendance.balance_last || '0',
                balance_current: attendance.balance_current || '0',
                ctc_month: '',
                ctc_year: '',
                remarks: '',
              });
          }
} catch (salaryError) {
            console.error('Error loading salary details:', salaryError);
            setPayslipFormData({
              basic: '',
              daily_allowance: '',
              house_rent_allowance: '',
              telephone_allowance: '',
              medical_allowance: '',
              food_allowance: '',
              others_allowance: '',
              overtime: '',
              loyalty_bonus: '',
              advance_payment: '',
              other_deduction: '',
              professional_tax: '',
              tds: '',
              epf: '',
              esi: '',
              work_days: attendance.work_days || '',
              present_days: attendance.present_days || '',
              absent_days: attendance.absent_days || '',
              sick_leave_taken: attendance.sick_leave_taken || '',
              paid_leave_taken: attendance.paid_leave_taken || '',
              unpaid_leave_taken: attendance.unpaid_leave_taken || '',
              leave_days: attendance.leave_days || '',
              unpaid_leave_days: attendance.unpaid_leave_days || '',
              leave_earned: attendance.leave_earned || '1',
              balance_last: attendance.balance_last || '0',
              balance_current: attendance.balance_current || '0',
              ctc_month: '',
              ctc_year: '',
              remarks: '',
            });
          }
        }
      
      setShowEmployeeList(false);
      setShowPayslipForm(true);
    } catch (error) {
      console.error('Error loading payslip for editing:', error);
      setError('Failed to load payslip data');
    }
  }, [selectedMonth, selectedYear, loadAttendanceSummary]);

  const handleBackToList = () => {
    setSelectedEmployee(null);
    setShowEmployeeList(true);
    setPayslip(null);
    setError('');
  };

  const handleFormSubmit = useCallback(async () => {
    if (formSubmitting) return;

    try {
      setFormSubmitting(true);
      setError('');

      // Validate employee selection
      if (!selectedEmployee || !selectedEmployee.id) {
        setError('Please select an employee first');
        setFormSubmitting(false);
        return;
      }

      // Validate required fields
      if (!payslipFormData.basic) {
        setError('Basic salary is required');
        setFormSubmitting(false);
        return;
      }

       // Prepare data for API
        const submitData = {
          employee_id: selectedEmployee.id,
          month: selectedMonth,
          year: selectedYear,
          basic: parseFloat(payslipFormData.basic) || 0,
          daily_allowance: parseFloat(payslipFormData.daily_allowance) || 0,
          house_rent_allowance: parseFloat(payslipFormData.house_rent_allowance) || 0,
          telephone_allowance: parseFloat(payslipFormData.telephone_allowance) || 0,
          medical_allowance: parseFloat(payslipFormData.medical_allowance) || 0,
          food_allowance: parseFloat(payslipFormData.food_allowance) || 0,
          others_allowance: parseFloat(payslipFormData.others_allowance) || 0,
          overtime: parseFloat(payslipFormData.overtime) || 0,
          loyalty_bonus: parseFloat(payslipFormData.loyalty_bonus) || 0,
          advance_payment: parseFloat(payslipFormData.advance_payment) || 0,
          other_deduction: parseFloat(payslipFormData.other_deduction) || 0,
          professional_tax: parseFloat(payslipFormData.professional_tax) || 0,
          tds: parseFloat(payslipFormData.tds) || 0,
          epf: parseFloat(payslipFormData.epf) || 0,
          esi: parseFloat(payslipFormData.esi) || 0,
          work_days: parseInt(payslipFormData.work_days) || 0,
          present_days: parseInt(payslipFormData.present_days) || 0,
          absent_days: parseInt(payslipFormData.absent_days) || 0,
          sick_leave_taken: parseFloat(payslipFormData.sick_leave_taken) || 0,
          paid_leave_taken: parseFloat(payslipFormData.paid_leave_taken) || 0,
          unpaid_leave_taken: parseFloat(payslipFormData.unpaid_leave_taken) || 0,
          leave_days: parseInt(payslipFormData.leave_days) || 0,
          unpaid_leave_days: parseInt(payslipFormData.unpaid_leave_days) || 0,
          leave_earned: parseFloat(payslipFormData.leave_earned) || 1,
          balance_last: parseFloat(payslipFormData.balance_last) || 10,
          balance_current: parseFloat(payslipFormData.balance_current) || 11,
          ctc_month: parseFloat(payslipFormData.ctc_month) || 0,
          ctc_year: parseFloat(payslipFormData.ctc_year) || 0,
          remarks: payslipFormData.remarks || '',
        };

      await payslipsAPI.create(submitData);
      
      setShowPayslipForm(false);
      setIsEditing(false);
      await loadPayslip();
      
    } catch (error) {
      console.error('Error saving payslip:', error);
      setError(error.response?.data?.message || 'Failed to save payslip details');
    } finally {
      setFormSubmitting(false);
    }
  }, [formSubmitting, payslipFormData, selectedEmployee, selectedMonth, selectedYear, loadPayslip]);

  const handlePreviousMonth = () => {
    let newMonth = selectedMonth - 1;
    let newYear = selectedYear;
    if (newMonth < 1) {
      newMonth = 12;
      newYear = selectedYear - 1;
    }
    setSelectedMonth(newMonth);
    setSelectedYear(newYear);
  };

  const handleNextMonth = () => {
    let newMonth = selectedMonth + 1;
    let newYear = selectedYear;
    if (newMonth > 12) {
      newMonth = 1;
      newYear = selectedYear + 1;
    }
    setSelectedMonth(newMonth);
    setSelectedYear(newYear);
  };

  const handleGenerateForPreviousMonth = async () => {
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth() + 1;
    const currentYear = currentDate.getFullYear();
    
    let targetMonth = currentMonth - 1;
    let targetYear = currentYear;
    
    if (targetMonth < 1) {
      targetMonth = 12;
      targetYear = currentYear - 1;
    }
    
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const confirmMessage = `This will generate payslips for all active employees for ${monthNames[targetMonth - 1]} ${targetYear}. This will sync all attendance, leave, and salary data for this month. Continue?`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }
    
    setGenerating(true);
    setError('');
    
    try {
      const response = await payslipsAPI.generateForMonth(targetMonth, targetYear);
      
      if (response.data.success) {
        const { generated, skipped, errors } = response.data.data;
        let message = `Payslip generation completed!\n\nGenerated: ${generated}\nSkipped (already exists): ${skipped}`;
        if (errors > 0) {
          message += `\nErrors: ${errors}`;
        }
        alert(message);
        
        if (generated > 0) {
          await loadPayslipList();
        }
      } else {
        setError(response.data.message || 'Failed to generate payslips');
      }
    } catch (err) {
      console.error('Error generating payslips:', err);
      setError(err.response?.data?.message || 'Failed to generate payslips');
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = () => {
    if (!payslip) return;

    const formatAmount = (amount) => {
      const num = parseFloat(amount) || 0;
      return num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const printWindow = window.open('', '_blank');

    if (printWindow) {
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Payslip - ${payslip.employee.name}</title>
            <style>
              * { margin: 0; padding: 0; box-sizing: border-box; }
              @page { size: A4; margin: 10mm; }
              body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; padding: 10px; background: #fff; font-size: 11px; line-height: 1.3; }
              .payslip-container { max-width: 210mm; margin: 0 auto; background: #fff; }
              .header { background: linear-gradient(135deg, #6e718d 0%, #303f9f 100%); color: white; padding: 12px 15px; text-align: center; display: flex; align-items: center; justify-content: space-between; }
              .header-left { display: flex; align-items: center; gap: 10px; }
              .logo { width: 40px; height: 40px; border-radius: 50%; background: white; padding: 3px; object-fit: contain; }
              .company-info { text-align: left; flex: 1; }
              .company-name { font-size: 16px; font-weight: 700; letter-spacing: 0.5px; text-transform: uppercase; }
              .company-address { font-size: 10px; opacity: 0.9; }
              .header-right { text-align: right; }
              .payslip-title { font-size: 13px; font-weight: 600; background: rgba(255,255,255,0.15); padding: 4px 12px; border-radius: 3px; }
              .title-bar { background: #fce4ec; padding: 8px 15px; border-bottom: 2px solid #e91e63; display: flex; justify-content: space-between; align-items: center; }
              .title-bar h2 { color: #c2185b; font-size: 14px; font-weight: 600; margin: 0; }
              .title-bar span { color: #666; font-size: 10px; }
              .content { padding: 12px 15px; }
              .employee-info { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 12px; }
              .info-card { background: #f8f9fa; border-left: 3px solid #1976d2; padding: 8px 10px; border-radius: 3px; }
              .info-card h4 { color: #1565c0; font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 6px; }
              .info-card p { font-size: 10px; color: #424242; margin-bottom: 3px; }
              .info-card strong { color: #212121; }
              .salary-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; margin-bottom: 12px; }
              .salary-table { width: 100%; border-collapse: collapse; font-size: 10px; }
              .salary-table th { color: white; padding: 6px 8px; text-align: left; font-size: 9px; text-transform: uppercase; }
              .income-table th { background: #2e7d32; }
              .deduction-table th { background: #c62828; }
              .salary-table td { padding: 4px 8px; border-bottom: 1px solid #e0e0e0; }
              .salary-table tr:nth-child(even) { background: #f8f9fa; }
              .salary-table .amount { text-align: right; font-family: 'Courier New', monospace; }
              .salary-table .total-row { font-weight: 700; }
              .income-table .total-row { background: #e8f5e9; }
              .income-table .total-row td { border-top: 1px solid #2e7d32; }
              .deduction-table .total-row { background: #ffcdd2; }
              .deduction-table .total-row td { border-top: 1px solid #c62828; }
              .summary-box { background: linear-gradient(135deg, #1a237e 0%, #303f9f 100%); color: white; padding: 10px 15px; border-radius: 4px; margin-bottom: 12px; }
              .summary-box h3 { font-size: 11px; opacity: 0.9; margin-bottom: 8px; }
              .summary-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; text-align: center; }
              .summary-item { background: rgba(255,255,255,0.1); padding: 8px; border-radius: 3px; }
              .summary-item .label { font-size: 9px; opacity: 0.8; margin-bottom: 3px; text-transform: uppercase; }
              .summary-item .value { font-size: 14px; font-weight: 700; }
              .summary-item.net-salary { background: #ffc107; color: #212121; }
              .summary-item.net-salary .value { font-size: 16px; }
              .attendance-section { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 10px; }
              .attendance-card { background: #f8f9fa; border-radius: 3px; padding: 8px; }
              .attendance-card h4 { color: #1565c0; font-size: 10px; margin-bottom: 6px; text-transform: uppercase; }
              .attendance-row { display: flex; justify-content: space-between; padding: 3px 0; border-bottom: 1px dashed #e0e0e0; font-size: 10px; }
              .attendance-row:last-child { border-bottom: none; }
              .footer { text-align: center; padding: 8px; border-top: 1px solid #e0e0e0; margin-top: 8px; }
              .footer p { font-size: 9px; color: #757575; margin: 2px 0; }
              .remarks { margin-top: 8px; padding: 6px; background: #fff3e0; border-radius: 3px; font-size: 10px; }
              ${getPayslipPrintStyles()}
            </style>
          </head>
          <body>
            <div class="payslip-container">
              <div class="header">
                <div class="header-left">
                  ${payslip.companyLogo ? `<img src="${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5076'}/uploads/companies/${payslip.companyLogo}" alt="Company Logo" class="logo" />` : ''}
                  <div class="company-info">
                    <div class="company-name">${payslip.companyName}</div>
                    <div class="company-address">${payslip.companyAddress}</div>
                  </div>
                </div>
                <div class="header-right">
                  <div class="payslip-title">Salary Slip</div>
                </div>
              </div>

              <div class="title-bar">
                <h2>Salary for ${payslip.salaryMonth} ${payslip.salaryYear}</h2>
                <span>ID: ${payslip.id} | ${payslip.salaryDate}</span>
              </div>

              <div class="content">
                <div class="employee-info">
                  <div class="info-card">
                    <h4>Employee Details</h4>
                    <p><strong>ID:</strong> ${payslip.employee.id} | <strong>Name:</strong> ${payslip.employee.name}</p>
                    <p><strong>Desig:</strong> ${payslip.employee.designation} | <strong>Join:</strong> ${payslip.employee.joiningDate}</p>
                  </div>
                  <div class="info-card">
                    <h4>Payment Details</h4>
                    <p><strong>PAN:</strong> ${payslip.employee.pan} | <strong>Mode:</strong> ${payslip.employee.paymentMode}</p>
                    <p><strong>Bank:</strong> ${payslip.employee.bankName} | <strong>A/C:</strong> ${payslip.employee.bankAccount}</p>
                  </div>
                </div>

                <div class="salary-grid">
                  <table class="salary-table income-table">
                    <thead>
                      <tr><th>Earnings</th><th style="text-align:right">Amount (₹)</th></tr>
                    </thead>
                    <tbody>
                      <tr><td>Basic Salary</td><td class="amount">${formatAmount(payslip.income.basic)}</td></tr>
                      ${payslip.income.dailyAllowance > 0 ? `<tr><td>Daily Allowance</td><td class="amount">${formatAmount(payslip.income.dailyAllowance)}</td></tr>` : ''}
                      ${payslip.income.houseRentAllowance > 0 ? `<tr><td>HRA</td><td class="amount">${formatAmount(payslip.income.houseRentAllowance)}</td></tr>` : ''}
                      ${payslip.income.medicalAllowance > 0 ? `<tr><td>Medical</td><td class="amount">${formatAmount(payslip.income.medicalAllowance)}</td></tr>` : ''}
                      ${payslip.income.telephoneAllowance > 0 ? `<tr><td>Telephone</td><td class="amount">${formatAmount(payslip.income.telephoneAllowance)}</td></tr>` : ''}
                      ${payslip.income.foodAllowance > 0 ? `<tr><td>Food Allowance</td><td class="amount">${formatAmount(payslip.income.foodAllowance)}</td></tr>` : ''}
                      ${payslip.income.othersAllowance > 0 ? `<tr><td>Others</td><td class="amount">${formatAmount(payslip.income.othersAllowance)}</td></tr>` : ''}
                      ${payslip.income.overtime > 0 ? `<tr><td>Overtime</td><td class="amount">${formatAmount(payslip.income.overtime)}</td></tr>` : ''}
                      ${payslip.income.loyaltyBonus > 0 ? `<tr><td>Loyalty Bonus</td><td class="amount">${formatAmount(payslip.income.loyaltyBonus)}</td></tr>` : ''}
                      <tr class="total-row"><td><strong>Gross Earnings</strong></td><td class="amount"><strong>₹${payslip.grossIncome?.toLocaleString('en-IN')}</strong></td></tr>
                    </tbody>
                  </table>

                  <table class="salary-table deduction-table">
                    <thead>
                      <tr><th>Deductions</th><th style="text-align:right">Amount (₹)</th></tr>
                    </thead>
                    <tbody>
                      ${payslip.advance_payment > 0 ? `<tr><td>Advance Payment</td><td class="amount">${formatAmount(payslip.advance_payment)}</td></tr>` : ''}
                      ${payslip.other_deduction > 0 ? `<tr><td>Other Deduction</td><td class="amount">${formatAmount(payslip.other_deduction)}</td></tr>` : ''}
                      ${payslip.professional_tax > 0 ? `<tr><td>Professional Tax</td><td class="amount">${formatAmount(payslip.professional_tax)}</td></tr>` : ''}
                      ${payslip.tds > 0 ? `<tr><td>TDS</td><td class="amount">${formatAmount(payslip.tds)}</td></tr>` : ''}
                      ${payslip.epf > 0 ? `<tr><td>EPF</td><td class="amount">${formatAmount(payslip.epf)}</td></tr>` : ''}
                      ${payslip.esi > 0 ? `<tr><td>ESI</td><td class="amount">${formatAmount(payslip.esi)}</td></tr>` : ''}
                      ${payslip.absence_deduction > 0 ? `<tr><td>Absence Deduction</td><td class="amount">${formatAmount(payslip.absence_deduction)}</td></tr>` : ''}
                      ${payslip.unpaid_leave_deduction > 0 ? `<tr><td>Unpaid Leave</td><td class="amount">${formatAmount(payslip.unpaid_leave_deduction)}</td></tr>` : ''}
                      <tr class="total-row"><td><strong>Total Deductions</strong></td><td class="amount"><strong>₹${payslip.grossDeduction?.toLocaleString('en-IN')}</strong></td></tr>
                    </tbody>
                  </table>
                </div>

                <div class="summary-box">
                  <h3>Salary Summary</h3>
                  <div class="summary-grid">
                    <div class="summary-item">
                      <div class="label">Gross Salary</div>
                      <div class="value">₹${payslip.grossIncome?.toLocaleString('en-IN')}</div>
                    </div>
                    <div class="summary-item">
                      <div class="label">Total Deductions</div>
                      <div class="value">₹${payslip.grossDeduction?.toLocaleString('en-IN')}</div>
                    </div>
                    <div class="summary-item net-salary">
                      <div class="label">Net Salary</div>
                      <div class="value">₹${payslip.netSalary?.toLocaleString('en-IN')}</div>
                    </div>
                  </div>
                </div>

                <div class="attendance-section">
                  <div class="attendance-card">
                    <h4>Attendance</h4>
                    <div class="attendance-row"><span>Work Days</span><strong>${payslip.attendance.workDays}</strong></div>
                    <div class="attendance-row"><span>Present</span><strong>${payslip.attendance.present}</strong></div>
                    <div class="attendance-row"><span>Absent</span><strong>${payslip.attendance.absent}</strong></div>
                  </div>
                  <div class="attendance-card">
                    <h4>Leave / CTC</h4>
                    <div class="attendance-row"><span>Unpaid Leave</span><strong>${payslip.leave?.unpaidLeaveTaken || 0}</strong></div>
                    <div class="attendance-row"><span>Total Leave</span><strong>${payslip.leave?.totalLeaveDays || 0}</strong></div>
                    <div class="attendance-row"><span>CTC/Month</span><strong>₹${parseFloat(payslip.ctcMonth || 0).toLocaleString('en-IN')}</strong></div>
                  </div>
                </div>

                ${payslip.remarks ? `<div class="remarks"><strong>Remarks:</strong> ${payslip.remarks}</div>` : ''}

                <div class="footer">
                  <p>This is a computer-generated document and does not require any signature.</p>
                </div>
              </div>
            </div>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  if (loading && !payslip && !showEmployeeList && !showPayslipList) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Render employee list for admin/HR
  if (isAdminOrHR && showEmployeeList) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Receipt color="primary" />
          Employee Payslip Management
        </Typography>
        {employees.length === 0 ? (
          <Alert severity="info">No employees found. Please create employees first.</Alert>
        ) : (
          <List>
            {employees.map((employee) => (
              <ListItem key={employee.id} disablePadding>
                <ListItemButton onClick={() => handleEmployeeSelect(employee)}>
                  <ListItemAvatar><Avatar>{employee.first_name?.[0] || 'E'}</Avatar></ListItemAvatar>
                  <ListItemText primary={`${employee.first_name} ${employee.last_name}`} secondary={`${employee.employee_id} - ${employee.designation || 'N/A'}`} />
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button variant="outlined" startIcon={<Visibility />} onClick={(e) => { e.stopPropagation(); handleEmployeeSelect(employee); }}>View</Button>
                    <Button variant="contained" startIcon={<Edit />} onClick={(e) => { e.stopPropagation(); handleEditPayslip(employee); }}>Edit</Button>
                  </Box>
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Receipt color="primary" />
          {isAdminOrHR ? 'Payslip Management' : 'Payslips'}
        </Typography>

        {isAdminOrHR && (
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button 
              variant="outlined" 
              onClick={() => handleGenerateForPreviousMonth()}
              startIcon={<Receipt />}
              disabled={generating}
              sx={{ bgcolor: '#02cc166d', color: 'rgb(10, 25, 41)', '&:hover': { bgcolor: '#197d00ba' } }}
            >
              {generating ? 'Generating...' : 'Generate Payslip for Previous Month'}
            </Button>
            {!showPayslipList && (
              <Button variant="contained" onClick={() => { setShowPayslipList(true); loadPayslipList(); }}>
                View Payslip List
              </Button>
            )}
            {showPayslipList && (
              <>
                <Button variant="outlined" onClick={() => setShowPayslipList(false)} startIcon={<Receipt />}>
                  Back to Payslip
                </Button>
                <Button 
                  variant={viewMode === 'list' ? 'contained' : 'outlined'} 
                  onClick={() => setViewMode('list')}
                  startIcon={<ListIcon />}
                >
                  List View
                </Button>
                <Button 
                  variant={viewMode === 'month' ? 'contained' : 'outlined'} 
                  onClick={() => setViewMode('month')}
                  startIcon={<CalendarViewMonth />}
                >
                  Month-wise
                </Button>
                {viewMode === 'month' && availableMonths.length > 0 && (
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Filter Month</InputLabel>
                    <Select 
                      value={selectedMonthFilter} 
                      label="Filter Month" 
                      onChange={(e) => setSelectedMonthFilter(e.target.value)}
                    >
                      <MenuItem value="all">All Months</MenuItem>
                      {availableMonths.map(m => (
                        <MenuItem key={m} value={m}>{payslipsByMonth.find(pm => `${pm.year}-${String(pm.month).padStart(2, '0')}` === m)?.monthName} {m.split('-')[0]}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </>
            )}
            {selectedEmployee && !showPayslipList && (
              <Button variant="outlined" onClick={handleBackToList}>
                Back to Employee List
              </Button>
            )}
            {selectedEmployee && !showPayslipList && (
              <Button variant="contained" startIcon={<Edit />} onClick={() => handleEditPayslip(selectedEmployee)}>
                Edit Payslip
              </Button>
            )}
            {payslip && !showPayslipList && (
              <Button variant="contained" startIcon={<Download />} onClick={handleDownload} color="secondary">
                Generate PDF
              </Button>
            )}
          </Box>
        )}
      </Box>

      {showPayslipList ? (
        <Box>
          {viewMode === 'list' ? (
            <>
              <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                <Typography variant="h6">Generated Payslips</Typography>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Sort By</InputLabel>
                  <Select value={sortBy} label="Sort By" onChange={(e) => setSortBy(e.target.value)}>
                    <MenuItem value="employee_id">Employee ID</MenuItem>
                    <MenuItem value="employee_name">Employee Name</MenuItem>
                    <MenuItem value="month">Month</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Employee ID</TableCell>
                      <TableCell>Employee Name</TableCell>
                      <TableCell>Month</TableCell>
                      <TableCell>Year</TableCell>
                      <TableCell align="right">Net Salary</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {payslipList
                      .filter(p => p && p.employee)
                      .sort((a, b) => {
                        if (sortBy === 'employee_id') {
                          return (a.employee?.employee_id || '').localeCompare(b.employee?.employee_id || '');
                        } else if (sortBy === 'employee_name') {
                          return `${a.employee?.first_name || ''} ${a.employee?.last_name || ''}`.localeCompare(`${b.employee?.first_name || ''} ${b.employee?.last_name || ''}`);
                        } else if (sortBy === 'month') {
                          return new Date(b.year, b.month - 1) - new Date(a.year, a.month - 1);
                        }
                        return 0;
                      })
                      .map((payslipItem) => (
                        <TableRow key={payslipItem.id}>
                          <TableCell>{payslipItem.employee?.employee_id || 'N/A'}</TableCell>
                          <TableCell>{`${payslipItem.employee?.first_name || ''} ${payslipItem.employee?.last_name || ''}`.trim() || 'N/A'}</TableCell>
                          <TableCell>{new Date(0, payslipItem.month - 1).toLocaleString('default', { month: 'long' })}</TableCell>
                          <TableCell>{payslipItem.year}</TableCell>
                          <TableCell align="right">₹{(payslipItem.net_salary || 0).toLocaleString('en-IN')}</TableCell>
                          <TableCell align="center">
                            <Button size="small" startIcon={<Visibility />} onClick={async () => {
                              const empWithId = { ...payslipItem.employee, id: payslipItem.employee_id };
                              setSelectedEmployee(empWithId);
                              await loadPayslip();
                              setShowPayslipList(false);
                            }}>View</Button>
                            <Button size="small" startIcon={<PictureAsPdf />} onClick={() => handleDownloadPayslip(payslipItem)} sx={{ ml: 1 }}>PDF</Button>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          ) : (
            <Box>
              {payslipsByMonth
                .filter(m => selectedMonthFilter === 'all' || `${m.year}-${String(m.month).padStart(2, '0')}` === selectedMonthFilter)
                .map((monthGroup) => (
                  <Box key={`${monthGroup.year}-${monthGroup.month}`} sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CalendarViewMonth color="primary" />
                      {monthGroup.monthName} {monthGroup.year}
                      <Chip label={`${monthGroup.payslips.length} payslips`} size="small" sx={{ ml: 1 }} />
                    </Typography>
                    <TableContainer component={Paper}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Employee ID</TableCell>
                            <TableCell>Employee Name</TableCell>
                            <TableCell>Designation</TableCell>
                            <TableCell align="right">Gross Salary</TableCell>
                            <TableCell align="right">Deductions</TableCell>
                            <TableCell align="right">Net Salary</TableCell>
                            <TableCell align="center">PDF</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {monthGroup.payslips
                            .filter(p => p && p.employee)
                            .sort((a, b) => `${a.employee?.first_name || ''} ${a.employee?.last_name || ''}`.localeCompare(`${b.employee?.first_name || ''} ${b.employee?.last_name || ''}`))
                            .map((payslipItem) => {
                              const grossIncome = (parseFloat(payslipItem.basic) || 0) +
                                (parseFloat(payslipItem.daily_allowance) || 0) +
                                (parseFloat(payslipItem.house_rent_allowance) || 0) +
                                (parseFloat(payslipItem.telephone_allowance) || 0) +
                                (parseFloat(payslipItem.medical_allowance) || 0) +
                                (parseFloat(payslipItem.food_allowance) || 0) +
                                (parseFloat(payslipItem.others_allowance) || 0) +
                                (parseFloat(payslipItem.overtime) || 0) +
                                (parseFloat(payslipItem.loyalty_bonus) || 0);
const grossDeduction = (parseFloat(payslipItem.advance_payment) || 0) +
                                 (parseFloat(payslipItem.other_deduction) || 0) +
                                 (parseFloat(payslipItem.professional_tax) || 0) +
                                 (parseFloat(payslipItem.tds) || 0) +
                                 (parseFloat(payslipItem.epf) || 0) +
                                 (parseFloat(payslipItem.esi) || 0) +
                                 (parseFloat(payslipItem.unpaid_leave_deduction) || 0) +
                                 (parseFloat(payslipItem.absence_deduction) || 0);
                              return (
                                <TableRow key={payslipItem.id}>
                                  <TableCell>{payslipItem.employee?.employee_id || 'N/A'}</TableCell>
                                  <TableCell>{`${payslipItem.employee?.first_name || ''} ${payslipItem.employee?.last_name || ''}`.trim() || 'N/A'}</TableCell>
                                  <TableCell>{payslipItem.employee?.designation || 'N/A'}</TableCell>
                                  <TableCell align="right">₹{grossIncome.toLocaleString('en-IN')}</TableCell>
                                  <TableCell align="right">₹{grossDeduction.toLocaleString('en-IN')}</TableCell>
                                  <TableCell align="right"><strong>₹{(grossIncome - grossDeduction).toLocaleString('en-IN')}</strong></TableCell>
                                  <TableCell align="center">
                                    <Button size="small" startIcon={<Visibility />} onClick={async () => {
                                      const empWithId = { ...payslipItem.employee, id: payslipItem.employee_id };
                                      setSelectedEmployee(empWithId);
                                      await loadPayslip();
                                      setShowPayslipList(false);
                                    }}>View</Button>
                                    <Button size="small" variant="contained" color="secondary" startIcon={<PictureAsPdf />} onClick={() => handleDownloadPayslip(payslipItem)} sx={{ ml: 1 }}>
                                      PDF
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                ))}
            </Box>
          )}
        </Box>
      ) : (
        <>
          <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <IconButton onClick={handlePreviousMonth}><ChevronLeft /></IconButton>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Month</InputLabel>
              <Select value={selectedMonth} label="Month" onChange={(e) => setSelectedMonth(e.target.value)}>
                {Array.from({ length: 12 }, (_, i) => (
                  <MenuItem key={i + 1} value={i + 1}>{new Date(0, i).toLocaleString('default', { month: 'long' })}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField label="Year" type="number" size="small" value={selectedYear} onChange={(e) => setSelectedYear(parseInt(e.target.value))} sx={{ width: 100 }} />
            <IconButton onClick={handleNextMonth}><ChevronRight /></IconButton>
            {isAdminOrHR && selectedEmployee && (
              <Chip label={`${selectedEmployee.first_name} ${selectedEmployee.last_name}`} avatar={<Avatar>{selectedEmployee.first_name?.[0] || 'E'}</Avatar>} />
            )}
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

          {/* Current Month Overview Section */}
          <Card sx={{ mb: 3, border: '2px solid #1976d2' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 800, display: 'flex', alignItems: 'center', gap: 1 }}>
                  📊 Current Month Overview
                </Typography>
                {isAdminOrHR && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Settings />}
                    onClick={() => setShowShiftConfig(true)}
                  >
                    Configure Working Days
                  </Button>
                )}
              </Box>

              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                {/* Attendance Rate */}
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ p: 2, bgcolor: 'rgb(10, 25, 41)', borderRadius: 2, textAlign: 'center' }}>
                    <Typography variant="body2" sx={{ color: '#cbd5e1', mb: 1 }}>Attendance Rate</Typography>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: '#4caf50', mb: 1 }}>
                      {currentMonthData?.attendanceRate || 0}%
                    </Typography>
                    <LinearProgress variant="determinate" value={currentMonthData?.attendanceRate || 0} sx={{ height: 6, borderRadius: 3 }} />
                  </Box>
                </Grid>

                {/* Working Days Breakdown */}
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ p: 2, bgcolor: 'rgb(10, 25, 41)', borderRadius: 2 }}>
                    <Typography variant="body2" sx={{ color: '#cbd5e1', mb: 1, fontWeight: 600 }}>Working Days</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#fff', mb: 0.5 }}>
                      {currentMonthData?.workingDays || 0}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#cbd5e1' }}>Configured: {shiftFormData.weekend_off ? 'Mon-Fri' : 'Custom'}</Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ p: 2, bgcolor: 'rgb(10, 25, 41)', borderRadius: 2 }}>
                    <Typography variant="body2" sx={{ color: '#cbd5e1', mb: 1, fontWeight: 600 }}>Weekend Days</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#2196f3', mb: 0.5 }}>
                      {currentMonthData?.weekendCount || 0}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#cbd5e1' }}>Sat & Sun Off</Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ p: 2, bgcolor: 'rgb(10, 25, 41)', borderRadius: 2 }}>
                    <Typography variant="body2" sx={{ color: '#cbd5e1', mb: 1, fontWeight: 600 }}>Total Days</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#ff9800', mb: 0.5 }}>
                      30-31
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#cbd5e1' }}>In Month</Typography>
                  </Box>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              {/* Attendance Summary */}
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1.5 }}>Attendance Summary</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ p: 1.5, bgcolor: 'rgb(10, 25, 41)', borderRadius: 1, textAlign: 'center' }}>
                    <Typography variant="body2" sx={{ color: '#fcfcfc', fontWeight: 600, mb: 0.5 }}>Present</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>{currentMonthData?.presentDays || 0}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ p: 1.5, bgcolor: 'rgb(10, 25, 41)', borderRadius: 1, textAlign: 'center' }}>
                    <Typography variant="body2" sx={{ color: '#ffffff', fontWeight: 600, mb: 0.5 }}>Late</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>{currentMonthData?.lateDays || 0}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ p: 1.5, bgcolor: 'rgb(10, 25, 41)', borderRadius: 1, textAlign: 'center' }}>
                    <Typography variant="body2" sx={{ color: '#f6f6f6', fontWeight: 600, mb: 0.5 }}>Absent</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>{currentMonthData?.absentDays || 0}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ p: 1.5, bgcolor: 'rgb(10, 25, 41)', borderRadius: 1, textAlign: 'center' }}>
                    <Typography variant="body2" sx={{ color: '#fbfbfb', fontWeight: 600, mb: 0.5 }}>Active Days</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>{currentMonthData?.activeDays || 0}</Typography>
                  </Box>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              {/* Work Time Summary */}
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1.5 }}>Work Time Summary</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ p: 1.5, bgcolor: 'rgb(10, 25, 41)', borderRadius: 1 }}>
                    <Typography variant="body2" sx={{ color: '#ffffff', mb: 0.5 }}>Total Work Hours</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>
                      {currentMonthData?.workHours ? 
                        `${Number(currentMonthData.workHours).toFixed(1)}h` 
                        : '0h'
                      }
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ p: 1.5, bgcolor: 'rgb(10, 25, 41)', borderRadius: 1 }}>
                    <Typography variant="body2" sx={{ color: '#ffffff', mb: 0.5 }}>Break Time</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>
                      {currentMonthData?.breakTime ? 
                        `${Number(currentMonthData.breakTime).toFixed(1)}h` 
                        : '0h'
                      }
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {payslip ? (
            <Card sx={{
              maxWidth: '940px',
              mx: 'auto',
              borderRadius: 2,
              overflow: 'hidden',
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: '0 18px 45px rgba(23, 32, 51, 0.12)'
            }}>
              <CardContent sx={{ p: 0 }}>
                {payslip.status === 'draft' && <Alert severity="warning" sx={{ mb: 1.5 }}>This payslip is in draft status.</Alert>}
                
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                  p: 3,
                  bgcolor: '#172033',
                  color: '#c9a227',
                  borderBottom: '4px solid #c9a227'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
                    {payslip.companyLogo ? (
                      <img src={`${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5076'}/uploads/companies/${payslip.companyLogo}`} alt="Logo" className="payslip-logo" />
                    ) : (
                      <Business sx={{ fontSize: 48, color: '#f7d765' }} />
                    )}
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: 0.4, textTransform: 'uppercase' }}>{payslip.companyName}</Typography>
                      <Typography variant="body2" sx={{ color: '#d9e0ea', mt: 0.5 }}>{payslip.companyAddress}</Typography>
                    </Box>
                  </Box>
                  <Box>
                    <Chip label="Salary Slip" sx={{ bgcolor: '#f7d765', color: '#172033', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 1 }} />
                  </Box>
                </Box>

                <Box sx={{ px: 3, py: 2, bgcolor: 'rgb(10, 25, 41)', borderBottom: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>Salary for {payslip.salaryMonth} {payslip.salaryYear}</Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>Payslip ID: {payslip.id} | Date: {payslip.salaryDate}</Typography>
                </Box>

                <Box sx={{ p: 3 }}>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'rgb(10, 25, 41)', height: '100%' }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.8 }}>Employee Details</Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}><strong>Employee ID:</strong> {payslip.employee.id}</Typography>
                      <Typography variant="body2"><strong>Employee Name:</strong> {payslip.employee.name}</Typography>
                      <Typography variant="body2"><strong>Designation:</strong> {payslip.employee.designation}</Typography>
                      <Typography variant="body2"><strong>Joining Date:</strong> {payslip.employee.joiningDate}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'rgb(10, 25, 41)', height: '100%' }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.8 }}>Payment Details</Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}><strong>PAN No:</strong> {payslip.employee.pan}</Typography>
                      <Typography variant="body2"><strong>Bank Name:</strong> {payslip.employee.bankName}</Typography>
                      <Typography variant="body2"><strong>Bank A/C No:</strong> {payslip.employee.bankAccount}</Typography>
                      <Typography variant="body2"><strong>Payment Mode:</strong> {payslip.employee.paymentMode}</Typography>
                    </Box>
                  </Grid>
                </Grid>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" sx={{ color: 'success.main', fontWeight: '600', mb: 0.5 }}>Income Per Month</Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableBody>
                          <TableRow><TableCell>Basic</TableCell><TableCell align="right">₹{(payslip.income.basic || 0).toLocaleString()}</TableCell></TableRow>
                          <TableRow><TableCell>Daily Allowance</TableCell><TableCell align="right">₹{(payslip.income.dailyAllowance || 0).toLocaleString()}</TableCell></TableRow>
                          <TableRow><TableCell>House Rent Allowance</TableCell><TableCell align="right">₹{(payslip.income.houseRentAllowance || 0).toLocaleString()}</TableCell></TableRow>
                          <TableRow><TableCell>Telephone Allowance</TableCell><TableCell align="right">₹{(payslip.income.telephoneAllowance || 0).toLocaleString()}</TableCell></TableRow>
                          <TableRow><TableCell>Medical Allowance</TableCell><TableCell align="right">₹{(payslip.income.medicalAllowance || 0).toLocaleString()}</TableCell></TableRow>
                          <TableRow><TableCell>Food Allowance</TableCell><TableCell align="right">₹{(payslip.income.foodAllowance || 0).toLocaleString()}</TableCell></TableRow>
                          <TableRow><TableCell>Others Allowance</TableCell><TableCell align="right">₹{(payslip.income.othersAllowance || 0).toLocaleString()}</TableCell></TableRow>
                          <TableRow><TableCell>Overtime</TableCell><TableCell align="right">₹{(payslip.income.overtime || 0).toLocaleString()}</TableCell></TableRow>
                          <TableRow><TableCell>Loyalty Bonus</TableCell><TableCell align="right">₹{(payslip.income.loyaltyBonus || 0).toLocaleString()}</TableCell></TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" sx={{ color: 'error.main', fontWeight: '600', mb: 0.5 }}>Deductions</Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableBody>
                          <TableRow><TableCell>Advance Payment</TableCell><TableCell align="right">₹{(payslip.advance_payment || 0).toLocaleString()}</TableCell></TableRow>
                          <TableRow><TableCell>Other Deduction</TableCell><TableCell align="right">₹{(payslip.other_deduction || 0).toLocaleString()}</TableCell></TableRow>
                          <TableRow><TableCell>Professional Tax</TableCell><TableCell align="right">₹{(payslip.professional_tax || 0).toLocaleString()}</TableCell></TableRow>
                          <TableRow><TableCell>TDS</TableCell><TableCell align="right">₹{(payslip.tds || 0).toLocaleString()}</TableCell></TableRow>
                          <TableRow><TableCell>EPF</TableCell><TableCell align="right">₹{(payslip.epf || 0).toLocaleString()}</TableCell></TableRow>
                          <TableRow><TableCell>ESI</TableCell><TableCell align="right">₹{(payslip.esi || 0).toLocaleString()}</TableCell></TableRow>
                          {payslip.absence_deduction > 0 && (
                            <TableRow sx={{ bgcolor: '#fff3e000' }}>
                              <TableCell><strong>Absence Deduction</strong></TableCell>
                              <TableCell align="right"><strong>₹{parseFloat(payslip.absence_deduction).toLocaleString()}</strong></TableCell>
                            </TableRow>
                          )}
                          {payslip.unpaid_leave_deduction > 0 && (
                            <TableRow sx={{ bgcolor: '#fff3e000' }}>
                              <TableCell><strong>Unpaid Leave Deduction</strong></TableCell>
                              <TableCell align="right"><strong>₹{parseFloat(payslip.unpaid_leave_deduction).toLocaleString()}</strong></TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                </Grid>

                <Box sx={{ mt: 2, p: 2, bgcolor: '#000b234e', borderRadius: 2, borderColor: 'divider', borderBottom: '1px solid',color: '#46b401b2' }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" sx={{ color: '#cbd5e1', textTransform: 'uppercase', letterSpacing: 0.8 }}>Gross Income</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 800 }}>₹{payslip.grossIncome?.toLocaleString('en-IN')}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" sx={{ color: '#cbd5e1', textTransform: 'uppercase', letterSpacing: 0.8 }}>Gross Deduction</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 800 }}>₹{payslip.grossDeduction?.toLocaleString('en-IN')}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Box sx={{ bgcolor: '#f7d76500', color: '#3b9c04cb', borderRadius: 2, p: 1.25 }}>
                        <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 800 }}>Net Payable Salary</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 900 }}>₹{payslip.netSalary?.toLocaleString('en-IN')}</Typography>
                      </Box>
                    </Grid>
                  </Grid>
                  <Typography variant="body2" sx={{ mt: 1.5, color: '#d9e0ea' }}>CTC/Month: ₹{payslip.ctcMonth?.toLocaleString('en-IN')} | CTC/Year: ₹{payslip.ctcYear?.toLocaleString('en-IN')}</Typography>
                </Box>

                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" sx={{ fontWeight: '600', mb: 0.5 }}>Attendance Details</Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableBody>
                          <TableRow><TableCell>Work Days</TableCell><TableCell align="right">{payslip.attendance.workDays}</TableCell></TableRow>
                          <TableRow><TableCell>Present Days</TableCell><TableCell align="right">{payslip.attendance.present}</TableCell></TableRow>
                          <TableRow><TableCell>Absent Days</TableCell><TableCell align="right">{payslip.attendance.absent}</TableCell></TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" sx={{ fontWeight: '600', mb: 0.5 }}>Leave Details</Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableBody>
                          <TableRow><TableCell>Sick Leave Taken</TableCell><TableCell align="right"><strong>{payslip.leave?.sickLeaveTaken || 0}</strong></TableCell></TableRow>
                          <TableRow><TableCell>Paid Leave Taken</TableCell><TableCell align="right"><strong>{payslip.leave?.paidLeaveTaken || 0}</strong></TableCell></TableRow>
                          <TableRow><TableCell>Unpaid Leave Taken</TableCell><TableCell align="right"><strong>{payslip.leave?.unpaidLeaveTaken || 0}</strong></TableCell></TableRow>
                          <TableRow><TableCell>Total Leave Days</TableCell><TableCell align="right">{payslip.leave?.totalLeaveDays || 0}</TableCell></TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                </Grid>

                <Box sx={{ mt: 2 }}><Typography variant="body2"><strong>Remarks:</strong> {payslip.remarks || 'N/A'}</Typography></Box>
                <Box sx={{ textAlign: 'center', borderTop: '1px dashed #ccc', pt: 2, mt: 2 }}>
                  <Typography variant="body2" sx={{ fontStyle: 'italic' }}>** This is a computer generated copy, no signature is required</Typography>
                </Box>
                </Box>
              </CardContent>
            </Card>
          ) : (
            <Alert severity="info">No payslip available for the selected period. Click "Edit Payslip" to create one.</Alert>
          )}
        </>
      )}

      <Dialog open={showPayslipForm} onClose={() => { setShowPayslipForm(false); setIsEditing(false); }} maxWidth="md" fullWidth>
        <DialogTitle>{isEditing ? 'Edit Payslip Details' : 'Create Payslip'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}><Typography variant="h6" color="success.main">Income Components</Typography></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Basic Salary *" type="number" value={payslipFormData.basic} onChange={(e) => setPayslipFormData({...payslipFormData, basic: e.target.value})} required /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Daily Allowance" type="number" value={payslipFormData.daily_allowance} onChange={(e) => setPayslipFormData({...payslipFormData, daily_allowance: e.target.value})} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="House Rent Allowance" type="number" value={payslipFormData.house_rent_allowance} onChange={(e) => setPayslipFormData({...payslipFormData, house_rent_allowance: e.target.value})} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Telephone Allowance" type="number" value={payslipFormData.telephone_allowance} onChange={(e) => setPayslipFormData({...payslipFormData, telephone_allowance: e.target.value})} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Medical Allowance" type="number" value={payslipFormData.medical_allowance} onChange={(e) => setPayslipFormData({...payslipFormData, medical_allowance: e.target.value})} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Food Allowance" type="number" value={payslipFormData.food_allowance} onChange={(e) => setPayslipFormData({...payslipFormData, food_allowance: e.target.value})} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Others Allowance" type="number" value={payslipFormData.others_allowance} onChange={(e) => setPayslipFormData({...payslipFormData, others_allowance: e.target.value})} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Overtime" type="number" value={payslipFormData.overtime} onChange={(e) => setPayslipFormData({...payslipFormData, overtime: e.target.value})} /></Grid>
             <Grid item xs={12} sm={6}><TextField fullWidth label="Loyalty Bonus" type="number" value={payslipFormData.loyalty_bonus} onChange={(e) => setPayslipFormData({...payslipFormData, loyalty_bonus: e.target.value})} /></Grid>

             <Grid item xs={12}><Typography variant="h6" color="success.main">CTC Details</Typography></Grid>
             <Grid item xs={12} sm={6}><TextField fullWidth label="CTC/Month" type="number" value={payslipFormData.ctc_month} onChange={(e) => setPayslipFormData({...payslipFormData, ctc_month: e.target.value})} /></Grid>
             <Grid item xs={12} sm={6}><TextField fullWidth label="CTC/Year" type="number" value={payslipFormData.ctc_year} onChange={(e) => setPayslipFormData({...payslipFormData, ctc_year: e.target.value})} /></Grid>

             <Grid item xs={12}><Typography variant="h6" color="error.main">Deductions</Typography></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Advance Payment" type="number" value={payslipFormData.advance_payment} onChange={(e) => setPayslipFormData({...payslipFormData, advance_payment: e.target.value})} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Other Deduction" type="number" value={payslipFormData.other_deduction} onChange={(e) => setPayslipFormData({...payslipFormData, other_deduction: e.target.value})} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Professional Tax" type="number" value={payslipFormData.professional_tax} onChange={(e) => setPayslipFormData({...payslipFormData, professional_tax: e.target.value})} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="TDS" type="number" value={payslipFormData.tds} onChange={(e) => setPayslipFormData({...payslipFormData, tds: e.target.value})} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="EPF" type="number" value={payslipFormData.epf} onChange={(e) => setPayslipFormData({...payslipFormData, epf: e.target.value})} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="ESI" type="number" value={payslipFormData.esi} onChange={(e) => setPayslipFormData({...payslipFormData, esi: e.target.value})} /></Grid>

            <Grid item xs={12}>
              <Typography variant="h6" color="warning.main">Attendance & Leave Details</Typography>
              <Alert severity="info" sx={{ mb: 2, mt: 1 }}>Absence Deduction = Absent Days × (Total Income / Work Days) | Unpaid Leave Deduction = Unpaid Leave Days × (Total Income / Work Days)</Alert>
            </Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="Work Days" type="number" value={payslipFormData.work_days} onChange={(e) => setPayslipFormData({...payslipFormData, work_days: e.target.value})} /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="Present Days" type="number" value={payslipFormData.present_days} onChange={(e) => setPayslipFormData({...payslipFormData, present_days: e.target.value})} /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="Absent Days" type="number" value={payslipFormData.absent_days} onChange={(e) => setPayslipFormData({...payslipFormData, absent_days: e.target.value})} /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="Sick Leave Taken" type="number" value={payslipFormData.sick_leave_taken} onChange={(e) => setPayslipFormData({...payslipFormData, sick_leave_taken: e.target.value})} /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="Paid Leave Taken" type="number" value={payslipFormData.paid_leave_taken} onChange={(e) => setPayslipFormData({...payslipFormData, paid_leave_taken: e.target.value})} /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="Unpaid Leave Taken" type="number" value={payslipFormData.unpaid_leave_taken} onChange={(e) => setPayslipFormData({...payslipFormData, unpaid_leave_taken: e.target.value})} /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="Unpaid Leave Days (Manual)" type="number" value={payslipFormData.unpaid_leave_days} onChange={(e) => setPayslipFormData({...payslipFormData, unpaid_leave_days: e.target.value})} /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="Total Leave Days (Manual)" type="number" value={payslipFormData.leave_days} onChange={(e) => setPayslipFormData({...payslipFormData, leave_days: e.target.value})} /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="Leave Earned" type="number" value={payslipFormData.leave_earned} onChange={(e) => setPayslipFormData({...payslipFormData, leave_earned: e.target.value})} /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="Balance Last Month" type="number" value={payslipFormData.balance_last} onChange={(e) => setPayslipFormData({...payslipFormData, balance_last: e.target.value})} /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="Balance Current Month" type="number" value={payslipFormData.balance_current} onChange={(e) => setPayslipFormData({...payslipFormData, balance_current: e.target.value})} /></Grid>

            <Grid item xs={12}><Typography variant="h6">Calculated Amounts</Typography></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="Total Income" type="number" value={totalIncome.toFixed(2)} InputProps={{ readOnly: true }} sx={{ '& .MuiInputBase-root': { bgcolor: 'rgb(10, 25, 41)' } }} /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="Per Day Income" type="number" value={perDayIncome.toFixed(2)} InputProps={{ readOnly: true }} sx={{ '& .MuiInputBase-root': { bgcolor: 'rgb(10, 25, 41)' } }} /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="Unpaid Leave Deduction (Auto)" type="number" value={unpaidLeaveDeduction.toFixed(2)} InputProps={{ readOnly: true }} sx={{ '& .MuiInputBase-root': { bgcolor: 'rgb(10, 25, 41)' } }} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Absence Deduction (Auto)" type="number" value={absenceDeductionCalc.toFixed(2)} InputProps={{ readOnly: true }} sx={{ '& .MuiInputBase-root': { bgcolor: 'rgb(10, 25, 41)' } }} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="CTC/Month (Auto)" type="number" value={ctcMonth} InputProps={{ readOnly: true }} sx={{ '& .MuiInputBase-root': { bgcolor: 'rgb(10, 25, 41)' } }} /></Grid>

            <Grid item xs={12}><TextField fullWidth label="Remarks" multiline rows={3} value={payslipFormData.remarks} onChange={(e) => setPayslipFormData({...payslipFormData, remarks: e.target.value})} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setShowPayslipForm(false); setIsEditing(false); }} disabled={formSubmitting}>Cancel</Button>
          <Button onClick={handleFormSubmit} variant="contained" disabled={formSubmitting || !selectedEmployee}>
            {formSubmitting ? <CircularProgress size={24} /> : (isEditing ? 'Update Payslip' : 'Save Payslip')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Shift Configuration Dialog */}
      <Dialog open={showShiftConfig} onClose={() => setShowShiftConfig(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>⚙️ Working Days Configuration</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>Weekend Settings</Typography>
            
            <FormControlLabel
              control={
                <Checkbox
                  checked={shiftFormData.weekend_off}
                  onChange={(e) => setShiftFormData({
                    ...shiftFormData,
                    weekend_off: e.target.checked
                  })}
                />
              }
              label={<Typography variant="body2">Weekend Off (Saturday & Sunday)</Typography>}
              sx={{ mb: 1 }}
            />

            {shiftFormData.weekend_off && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={shiftFormData.first_saturday_work}
                    onChange={(e) => setShiftFormData({
                      ...shiftFormData,
                      first_saturday_work: e.target.checked
                    })}
                  />
                }
                label={<Typography variant="body2">First Saturday is Working Day</Typography>}
                sx={{ mb: 2, ml: 2 }}
              />
            )}

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>Select Working Days</Typography>
            <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 1 }}>
              Applies to: {new Date(0, selectedMonth - 1).toLocaleString('default', { month: 'long' })} {selectedYear}
            </Typography>

            <FormGroup>
              {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day) => (
                <FormControlLabel
                  key={day}
                  control={
                    <Checkbox
                      checked={shiftFormData.working_days.includes(day)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setShiftFormData({
                            ...shiftFormData,
                            working_days: [...shiftFormData.working_days, day]
                          });
                        } else {
                          setShiftFormData({
                            ...shiftFormData,
                            working_days: shiftFormData.working_days.filter(d => d !== day)
                          });
                        }
                      }}
                      disabled={shiftFormData.weekend_off && (day === 'saturday' || day === 'sunday')}
                    />
                  }
                  label={<Typography variant="body2">{day.charAt(0).toUpperCase() + day.slice(1)}</Typography>}
                />
              ))}
            </FormGroup>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ p: 2, bgcolor: '#192853', borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>📅 Working Days Calculation</Typography>
              <Typography variant="body2" sx={{ color: '#fff1f1', mb: 0.5 }}>
                <strong>Month:</strong> {new Date(0, selectedMonth - 1).toLocaleString('default', { month: 'long' })} {selectedYear}
              </Typography>
              <Typography variant="body2" sx={{ color: '#fffbfb', mb: 0.5 }}>
                <strong>Working Days:</strong> {currentMonthData?.workingDays || 0}
              </Typography>
              <Typography variant="body2" sx={{ color: '#fff8f8', mb: 0.5 }}>
                <strong>Weekend Days:</strong> {currentMonthData?.weekendCount || 0}
              </Typography>
              <Typography variant="body2" sx={{ color: '#fff8f8' }}>
                <strong>Configuration:</strong> {shiftFormData.weekend_off ? 'Weekends Off' : 'Custom Days'} {shiftFormData.first_saturday_work && '+ First Saturday Working'}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowShiftConfig(false)}>Cancel</Button>
          <Button
            onClick={() => {
              // Update current month data based on new shift config
              const workingDaysData = calculateWorkingDaysInMonth(selectedMonth, selectedYear, shiftFormData);
              setCurrentMonthData({
                ...currentMonthData,
                workingDays: workingDaysData.workingDays,
                weekendCount: workingDaysData.weekendCount,
                attendanceRate: 0
              });
              setShowShiftConfig(false);
            }}
            variant="contained"
          >
            Save Configuration
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Payslip;
